import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface IUseOverflowDetectionResult {
  hasOverflow: boolean;
}

function useOverflowDetection(ref: RefObject<HTMLElement | null>): IUseOverflowDetectionResult {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [currentNode, setCurrentNode] = useState<HTMLElement | null>(null);
  const animationFrameId = useRef<number | null>(null);

  const checkOverflow = useCallback(() => {
    if (!ref.current) {
      setHasOverflow(false);
      return;
    }
    const node = ref.current;
    const overflow = node.scrollHeight > node.clientHeight;
    setHasOverflow(overflow);
  }, [ref]);

  useEffect(() => {
    const checkForNodeChange = (): void => {
      if (ref.current !== currentNode) {
        setCurrentNode(ref.current);
      }
      animationFrameId.current = requestAnimationFrame(checkForNodeChange);
    };

    animationFrameId.current = requestAnimationFrame(checkForNodeChange);

    return () => {
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [ref, currentNode]);

  useEffect(() => {
    checkOverflow();

    if (!currentNode) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    const mutationObserver = new MutationObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(currentNode);
    mutationObserver.observe(currentNode, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [checkOverflow, currentNode]);

  return { hasOverflow };
}

export default useOverflowDetection;
